import React, { useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { BiLoaderCircle } from 'react-icons/bi';
import { Icons } from '@/utils/Icons';
import CustomInput from '@/components/common/CustomInput';
import PhoneInput from '@/components/common/PhoneInput';
import OtpTimer from '@/components/common/OtpTimer';
import { useForgotPassword } from '@/pages/Auth/hooks/useForgotPassword';
import { useLockBodyScroll } from '@/hooks';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal = ({ isOpen, onClose }: ForgotPasswordModalProps) => {
  const { t } = useTranslation();
  const {
    form,
    phone,
    setPhone,
    showOtpField,
    timeLeft,
    active,
    otpLoading,
    resetLoading,
    handleSendOtp,
    handleResetOtpField,
    handleSubmitForm,
  } = useForgotPassword({ onSuccess: onClose });

  useLockBodyScroll(isOpen);

  const { control, handleSubmit, reset } = form;

  const initialPhone = {
    country: '',
    countryCode: '',
    fullNumber: '',
    phoneNumber: '',
  };

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const handleClose = () => {
    reset();
    handleResetOtpField();
    setPhone(initialPhone);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-2 md:px-4"
      onMouseDown={(e) => {
        mouseDownTarget.current = e.target;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-[400px] min-h-[300px] rounded-[10px] border-[3px] border-[#ff9500]  p-[14px] text-xs shadow-2xl bg-white dark:bg-[#18162A]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-3 top-3 text-black dark:text-white transition hover:text-red-500"
        >
          <Icons.Cross2 size={12} />
        </button>

        {/* Header */}
        <h2 className="mb-3 text-center text-base font-semibold dark:text-white text-black">
          {t('auth.forgotPassword.title')}
        </h2>
        <div className="mb-6 border-b-2 border-black dark:border-white" />

        <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="mobile" className="text-sm text-black dark:text-white">
              {t('auth.phoneNumber')}
            </label>

            <Controller
              name="mobile"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <>
                  <div className="flex gap-2">
                    <PhoneInput
                      value={field.value}
                      onChange={(phoneData) => {
                        setPhone(phoneData);
                        return field.onChange(phoneData.fullNumber);
                      }}
                      disabled={active}
                    />

                    <button
                      type="button"
                      onClick={active ? handleResetOtpField : handleSendOtp}
                      disabled={otpLoading}
                      className="shrink-0 rounded-[4px] border border-orange-400 bg-[#08061A] px-3 text-xs font-semibold text-white transition enabled:hover:bg-white enabled:hover:text-black disabled:opacity-60"
                    >
                      {otpLoading ? (
                        <BiLoaderCircle className="animate-spin" size={16} />
                      ) : active ? (
                        t('auth.changeNumber')
                      ) : (
                        t('auth.sendOtp')
                      )}
                    </button>
                  </div>
                  {error && <p className="text-xs text-red-600">{error.message}</p>}
                </>
              )}
            />

            {showOtpField && (
              <div className="w-fit ml-auto">
                <OtpTimer timeLeft={timeLeft} active={active} onResend={handleSendOtp} />
              </div>
            )}
          </div>

          <CustomInput
            name="otp"
            control={control}
            type="text"
            inputType="numeric"
            placeholder={t('auth.otpPlaceholder')}
            maxLength={6}
            disabled={!showOtpField}
          />

          <CustomInput
            name="newPassword"
            control={control}
            type="password"
            placeholder={t('auth.forgotPassword.newPasswordPlaceholder')}
            maxLength={20}
            disabled={!showOtpField}
          />

          <CustomInput
            name="confirmPassword"
            control={control}
            type="password"
            placeholder={t('auth.forgotPassword.confirmPasswordPlaceholder')}
            maxLength={20}
            disabled={!showOtpField}
          />

          {/* Buttons */}
          <div className="pt-1">
            <button
              type="submit"
              disabled={resetLoading || !showOtpField}
              className="flex h-11 w-full items-center justify-center rounded-xl border border-orange-400 bg-[#08061A] text-lg font-semibold text-white [filter:drop-shadow(1px_2px_4px_#ffd04e)] transition hover:bg-white hover:text-black disabled:opacity-60 "
            >
              {resetLoading ? (
                <BiLoaderCircle className="animate-spin" size={20} />
              ) : (
                t('auth.forgotPassword.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ForgotPasswordModal;
